import { IconX } from 'featherico';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import type { Model } from '../../../common/models/popup-alert';
import singleton from '../../../common/models/popup-alert/singleton';
import history from '../../../routing/history';
import { AriaAnnouncer } from '../AriaHelper';
import { InputButtonIcon } from '../Input/InputButton';
import PopupAlertStatus from './PopupAlertStatus';
import styles from './style.m.less';

type ContainerProps = {
    mountNode: HTMLElement;
};

type Props = {
    model: Model;
    onDismiss: () => void;
};

const PopupAlert: React.FC<Props> = ({ model, onDismiss }) => {
    const style = useSpring({ opacity: model.isOpen ? 1 : 0, transform: `translateY(${model.isOpen ? 0 : -200}px)` });
    return (
        <animated.div className={styles.popup_alert} style={style}>
            <InputButtonIcon className={styles.dismiss} onClick={onDismiss} aria-hidden>
                <IconX />
            </InputButtonIcon>
            <PopupAlertStatus status={model.status}>{model.message}</PopupAlertStatus>
        </animated.div>
    );
};

const PopupAlertContainer: React.FC<ContainerProps> = ({ mountNode }) => {
    const [model, setModel] = useState(singleton.getModel());

    useEffect(() => {
        mountNode.classList.add(styles.outerContainer);
        return () => mountNode.classList.remove(styles.outerContainer);
    }, [mountNode]);

    useEffect(() => {
        if (!history) {
            return;
        }

        return history.listen(() => {
            if (!singleton.getModel().surviveNavigation) {
                singleton.dismiss();
            }
        });
    }, []);

    useEffect(() => {
        const handler = (newModel: Model) => {
            setModel(oldModel => {
                if (oldModel.isOpen) {
                    setTimeout(() => {
                        handler(newModel);
                    }, 1000);
                    return { ...oldModel, isOpen: false };
                }
                return newModel;
            });
        };
        singleton.subscribe(handler);
        return () => singleton.unsubscribe(handler);
    }, []);

    return (
        <AriaAnnouncer hiddenFromView={false} ariaRole="region" ariaLive="assertive" isAtomic={true}>
            <div className={styles.innerContainer}>
                <PopupAlert model={model} onDismiss={() => singleton.dismiss()} />
            </div>
        </AriaAnnouncer>
    );
};

export default PopupAlertContainer;
